import {collection, doc, getDocs, getFirestore, Timestamp} from 'firebase/firestore'
import {AddRiskModal} from '../AddRiskModal'
import {saveAs} from 'file-saver'
import {useAuth} from '../../../../modules/auth'
import ExcelJS from 'exceljs'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ExportButton from 'app/components/ExportButton'
import { format } from 'date-fns'

const RiskListToolbar = ({currentAuditContext}) => {
  const {currentUser} = useAuth()

  const exportData = async () => {
    const db = getFirestore()
    const tenantId = currentUser?.tenantId
    const policyRef = collection(doc(db, 'tenants', tenantId!), 'risks')
    const querySnapshot = await getDocs(policyRef)

    const data = querySnapshot.docs.map((doc) => doc.data())

    if (data.length > 0) {
      const fields = [
        {key: 'riskSource', header: 'Risk Name', width: 50},
        {key: 'description', header: 'Description', width: 40},
        {key: 'category', header: 'Category', width: 40},
        {key: 'owner', header: 'Risk Owner', width: 20},
        {key: 'CIA', header: 'CIA', width: 30},
        {key: 'inherent_likelihood', header: 'Inherent Likelihood', width: 15},
        {key: 'inherent_impact', header: 'Inherent Impact', width: 15},
        {key: 'residual_impact', header: 'Residual Impact', width: 15},
        {key: 'residual_likelihood', header: 'Residual Likelihood', width: 15},
        {key: 'treatment', header: 'Treatment', width: 15},
        {key: 'risk_status', header: 'Risk Status', width: 15},
        {key: 'control_title', header: 'Mapped Controls', width: 30},
        {key: 'created_at', header: 'Created At', width: 20},
        {key: 'updated_at', header: 'Updated At',  width: 20},
      ]

      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Risks')

      worksheet.columns = fields.map((field) => ({
        header: field.header,
        key: field.key,
        width: field.width,
      }))

      const headerRow = worksheet.getRow(1)
      headerRow.font = {bold: true, color: {argb: 'FFFFFFFF'}, size: 10}
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {argb: 'FF000000'},
      }
      headerRow.height = 30
      headerRow.alignment = {horizontal: 'center', vertical: 'middle'}
      worksheet.views = [{state: 'frozen', ySplit: 1}]

      data.forEach((row) => {
        const rowData = fields.reduce((acc, field) => {
          if (field.key === 'CIA') {
            acc[field.key] = (row[field.key] || []).join(', ')
          } else if (field.key === 'category') {
            acc[field.key] = (row[field.key] || []).join(', ')
          } else if (field.key === 'control_title') {
            acc[field.key] = (row.added_controls || [])
              .map((control) => control.control_title)
              .join(', ')
          } else if (field.key ==='created_at' || field.key === 'updated_at') {
            const timestamp =
              row[field.key] instanceof Timestamp ? row[field.key].toDate() : row[field.key]
            acc[field.key] = timestamp ? format(timestamp,'yyyy-MM-dd') : ''
          } else {
            acc[field.key] = row[field.key] || ''
          }
          return acc
        }, {})

        const addedRow = worksheet.addRow(rowData)
        addedRow.height = 20
        addedRow.font = {size: 10}

        const riskStatusCell = addedRow.getCell('risk_status')
        switch (riskStatusCell.value) {
          case 'Incomplete':
            riskStatusCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: {argb: 'F4CCCC'},
            }
            break
          case 'Needs approval':
            riskStatusCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: {argb: 'FFF2CC'},
            }
            break
          case 'Approved':
            riskStatusCell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: {argb: 'D9EAD3'},
            }
            break

          default:
            break
        }
      })

      const buffer = await workbook.xlsx.writeBuffer()
      const excelBlob = new Blob([buffer], {type: 'application/octet-stream'})
      saveAs(excelBlob, 'risks.xlsx')
      showSuccessToast({message: 'Risk data exported successfully'})
    }else {
      if (typeof showSuccessToast === 'function') {
        showErrorToast({message: 'No data to export'})
      } 
      return
    }
  }

  return (
    <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
      {currentUser?.role !== 'Auditor' && !currentAuditContext && (
      <ExportButton className='me-4' onClick={exportData} />
      )}
      <AddRiskModal currentAuditContext={currentAuditContext} />
    </div>
  )
}

export {RiskListToolbar}
